import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, RefreshCw, Users, TrendingUp, Eye, Link2 } from "lucide-react";
import api, { formatApiError } from "../api/client";
import { AdminTabs } from "./AdminDashboard";
import { toast } from "sonner";

export default function AdminAnalytics() {
    const [d, setD] = useState(null);
    const [refreshing, setRefreshing] = useState(false);

    const load = async () => {
        setRefreshing(true);
        try { const r = await api.get("/admin/analytics"); setD(r.data); }
        catch (e) { toast.error("failed to load analytics", { description: formatApiError(e.response?.data?.detail) }); }
        finally { setRefreshing(false); }
    };
    useEffect(() => { load(); const id = setInterval(load, 30000); return () => clearInterval(id); }, []);

    return (
        <div className="bg-[#050505] min-h-[calc(100vh-3.5rem)] bg-grid-fine">
            <div className="max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-10 grid gap-6">
                <div className="flex items-center justify-between flex-wrap gap-3">
                    <div>
                        <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-cyan-400">Admin · Analytics</div>
                        <h1 className="mt-2 text-2xl sm:text-3xl font-medium tracking-tighter">Platform analytics</h1>
                        {d && <div className="mt-1 text-[10px] font-mono text-zinc-500">Generated {new Date(d.generated_at).toLocaleString()}</div>}
                    </div>
                    <div className="flex items-center gap-2">
                        <button onClick={load} disabled={refreshing} data-testid="analytics-refresh"
                            className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.18em] text-zinc-300 hover:text-cyan-400 border border-[#1F1F24] hover:border-cyan-500/40 px-3 py-2 rounded-sm disabled:opacity-40">
                            <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? "animate-spin" : ""}`} /> Refresh
                        </button>
                        <Link to="/admin" data-testid="analytics-back"
                            className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] text-zinc-400 hover:text-cyan-400 border border-[#1F1F24] hover:border-cyan-500/40 px-3 py-2 rounded-sm">
                            <ArrowLeft className="w-3.5 h-3.5" /> Admin
                        </Link>
                    </div>
                </div>
                <AdminTabs />

                {!d && <div className="text-[11px] font-mono text-zinc-500">loading…</div>}
                {d && (
                    <>
                        <Section icon={Users} title="Users">
                            <Stat label="Total" value={d.users.total} testid="stat-users-total" />
                            <Stat label="Admins" value={d.users.admins} />
                            <Stat label="Bots" value={d.users.bots} />
                            <Stat label="Disabled" value={d.users.disabled} accent="text-rose-400" />
                            <Stat label="New 24h" value={d.users.new_24h} accent="text-cyan-400" />
                            <Stat label="New 7d" value={d.users.new_7d} />
                            <Stat label="New 30d" value={d.users.new_30d} />
                            <Stat label="Active 24h" value={d.users.active_24h} accent="text-emerald-400" />
                            <Stat label="Active 7d" value={d.users.active_7d} />
                        </Section>

                        <Section icon={Eye} title="Visitors (anonymised)">
                            <Stat label="Total hits" value={d.visitors.total} testid="stat-visitors-total" />
                            <Stat label="Last 24h" value={d.visitors.last_24h} accent="text-cyan-400" />
                            <Stat label="Last 7d" value={d.visitors.last_7d} />
                            <Stat label="Unique IPs 24h" value={d.visitors.unique_ips_24h} accent="text-emerald-400" />
                        </Section>

                        <Section icon={TrendingUp} title="Swaps">
                            <Stat label="Total" value={d.swaps.total} testid="stat-swaps-total" />
                            <Stat label="Active" value={d.swaps.active} accent="text-cyan-400" />
                            <Stat label="Settled" value={d.swaps.settled} />
                            <Stat label="24h" value={d.swaps.last_24h} accent="text-emerald-400" />
                            <Stat label="Volume USDC" value={Number(d.swaps.lifetime_volume_usdc || 0).toLocaleString()} />
                            <Stat label="Fees USDC" value={Number(d.swaps.lifetime_fees_usdc || 0).toFixed(2)} accent="text-violet-400" />
                        </Section>

                        {d.swaps.top_coins?.length > 0 && (
                            <div className="bg-[#0A0A0C] border border-[#1F1F24] rounded-sm p-5">
                                <div className="text-[10px] font-mono uppercase tracking-[0.22em] text-zinc-500 mb-3">Top coins by volume</div>
                                <table className="w-full font-mono text-xs">
                                    <thead><tr className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 border-b border-[#1F1F24]">
                                        <th className="text-left py-2">Coin</th>
                                        <th className="text-right py-2">Swaps</th>
                                        <th className="text-right py-2">Volume USDC</th>
                                    </tr></thead>
                                    <tbody>
                                        {d.swaps.top_coins.map((c) => (
                                            <tr key={c.coin} className="border-t border-[#1F1F24]/60">
                                                <td className="py-2 text-zinc-200">{c.coin}</td>
                                                <td className="py-2 text-right text-zinc-300">{c.swaps.toLocaleString()}</td>
                                                <td className="py-2 text-right text-cyan-300">{Number(c.volume).toLocaleString()}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}

                        <Section icon={Link2} title="On-chain (Hyperliquid testnet)">
                            <Stat label="Total events" value={d.onchain.total} testid="stat-onchain-total" />
                            <Stat label="Succeeded" value={d.onchain.succeeded} accent="text-emerald-400" />
                            <Stat label="Last 24h" value={d.onchain.last_24h} accent="text-cyan-400" />
                            <Stat label="Success rate" value={d.onchain.success_rate != null ? `${(d.onchain.success_rate * 100).toFixed(1)}%` : "—"} />
                        </Section>

                        {d.deposit_requests?.pending > 0 && (
                            <Link to="/admin/deposits" data-testid="analytics-deposits-link"
                                className="block bg-amber-500/10 border border-amber-500/40 rounded-sm px-4 py-3 hover:bg-amber-500/15">
                                <div className="text-sm font-medium text-amber-300">
                                    {d.deposit_requests.pending} pending deposit request{d.deposit_requests.pending === 1 ? "" : "s"} →
                                </div>
                            </Link>
                        )}
                    </>
                )}
            </div>
        </div>
    );
}

function Section({ icon: Icon, title, children }) {
    return (
        <div>
            <div className="flex items-center gap-2 mb-3">
                <Icon className="w-4 h-4 text-cyan-400" />
                <div className="text-sm font-medium tracking-tight">{title}</div>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">{children}</div>
        </div>
    );
}
function Stat({ label, value, accent = "text-zinc-100", testid }) {
    return (
        <div className="bg-[#0A0A0C] border border-[#1F1F24] p-4 rounded-sm" data-testid={testid}>
            <div className="text-[10px] font-mono uppercase tracking-[0.22em] text-zinc-500">{label}</div>
            <div className={`mt-2 font-mono text-2xl tracking-tighter ${accent}`}>{value ?? "—"}</div>
        </div>
    );
}
